import _ from 'lodash'
import React, { useState } from 'react'
import { Typography } from '@mui/material'
import { FreeMode, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { useNavigate } from 'react-router-dom'

//constants
import { ANALYTICS_EVENT } from 'constants/index'
//components
import InfoFeeModal from '../InfoFeeModal'
import GiftCard from '../GiftCard'
//hooks
import useCardsSlider from 'hooks/useCardsSlider'
//types
import { GiftCardRowProps } from 'types'
//styles
import './index.css'
//utils
import { BackArrow as ArrowIcon, Info } from 'utils/icons'
import { sendGA4Event } from 'utils/utils'

const GiftCardsRow = ({ giftCard }: GiftCardRowProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const handleShowModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)

  const swiper = useCardsSlider(giftCard.denomination.length)

  const navigate = useNavigate()

  const handleSwiperClick = (swiper: SwiperClass, event: MouseEvent) => {
    const element = (event.target as HTMLElement)?.closest('.giftCardListCard')
    const denomination = element instanceof HTMLElement ? element.dataset?.denomination : undefined

    if (denomination) {
      sendGA4Event(ANALYTICS_EVENT.SELECT_ITEM.GIFT_CARD, {
        item_id: `${_.snakeCase(giftCard.name)}_${denomination}`,
        item_brand: giftCard.name,
        price: denomination,
      })

      navigate(`/rewards/${giftCard.id}/${denomination}`)
    }
  }

  const sliderSettings = {
    className: 'gamesListOffersRowSlider',
    freeMode: true,
    modules: [FreeMode, Navigation],
    spaceBetween: 20,
    slidesPerView: 1,
    navigation: true,
    width: 216,
    ref: swiper.positionRef,
    onSwiper: swiper.setSwiper,
    onClick: handleSwiperClick,
    onSlideChange: swiper.changeButtonStates,
  }

  return (
    <>
      <Typography variant="inherit" sx={styles.title(giftCard.fee)}>
        {giftCard.name}
      </Typography>
      {Number.isFinite(giftCard.fee) && (
        <Typography variant="h4" sx={styles.textFee}>
          Costs include additional fees charged by {giftCard.name}{' '}
          <Info onClick={handleShowModal} />
        </Typography>
      )}
      <div ref={swiper.wrapperRef} className="giftCardsRow">
        <div
          className={`gamesListOffersRowArrow ${!swiper.isPrevEnabled ? 'disabled' : ''} prev`}
          onClick={() => swiper.handleClickArrow(false)}
        >
          <ArrowIcon />
        </div>
        <Swiper {...sliderSettings}>
          {giftCard.denomination.map((denomination, index) => (
            <SwiperSlide key={index}>
              <GiftCard
                giftCard={giftCard}
                denomination={denomination.value}
                discount={denomination.discount}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className={`gamesListOffersRowArrow ${!swiper.isNextEnabled ? 'disabled' : ''} next`}
          onClick={() => swiper.handleClickArrow(true)}
        >
          <ArrowIcon />
        </div>
      </div>
      <InfoFeeModal
        showModal={isModalOpen}
        toggleModal={handleCloseModal}
        giftCardName={giftCard.name}
        minFeeValue={giftCard.minFeeValue}
      />
    </>
  )
}

const styles = {
  title: (fee?: number) => ({
    marginBottom: fee ? '2px' : '12px',
    paddingLeft: '20px',
  }),
  textFee: {
    color: '#636874',
    fontWeight: 400,
    paddingLeft: '20px',
    fontSize: '15px',
    marginBottom: '10px',
  },
}

export default GiftCardsRow
